import { useEffect, useMemo } from "react";
import { Chip } from "@mui/material";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUserActions } from "../../hooks/useUserActions.jsx";
import PropTypes from "prop-types";
import {
  AddCircleOutlineOutlined,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { userOptions } from "../../pages/users/components/AdminUserFormDefinitions.js";

// Kunden-Rollen sind als "Kunde (X)" beschriftet (z.B. "Kunde (Verwaltung)");
// für Kunden-Nutzer selbst ist der "Kunde"-Zusatz redundant und wird entfernt.
const roleLabel = (role, forCustomerViewer) => {
  const label = userOptions.rolle.find((option) => option.key === role)?.label || role;
  return forCustomerViewer ? label.replace(/^Kunde \((.+)\)$/, "$1") : label;
};

export default function UsersTable({
  onRowEdit = () => {},
  onClickNew = () => {},
  onClickImport = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const navigate = useNavigate();
  const { state: usersState, setState: setUsersState } = useUserActions();

  const tableKey = "users";
  const storageKey = "v2_table_users";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const is_sys_admin = user?.role_names?.includes("sysadmin");
  const is_customer_admin = user?.role_names?.includes("kd_verwaltung");
  const can_manage_users = !!(is_sys_admin || is_customer_admin);
  const can_import_users = !!is_sys_admin;

  const rows = usersState?.data?.data || [];

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Benutzer",
        route: `/data/users`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "email", order: "asc" }],
        },
        onStateChange: setUsersState,
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toolbarActions = [
    {
      type: "button",
      label: "Neuer Benutzer",
      icon: <AddCircleOutlineOutlined />,
      onClick: onClickNew,
      variant: "primary",
    },
    ...(can_import_users
      ? [
          {
            type: "button",
            label: "Benutzer importieren",
            icon: <UploadFileIcon />,
            onClick: onClickImport,
            variant: "tertiary",
          },
        ]
      : []),
  ];

  const columns = [
    {
      accessorKey: "email",
      header: "E-Mail",
      size: 250,
    },
    {
      accessorKey: "vorname",
      header: "Vorname",
    },
    {
      accessorKey: "nachname",
      header: "Nachname",
    },
    {
      accessorKey: "rolle",
      header: "Rolle",
      Cell: ({ row }) => (
        <Chip
          label={roleLabel(row.original.rolle, is_customer_admin)}
          size="small"
        />
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 80,
    },
    ...(is_customer_admin
      ? []
      : [
          {
            accessorFn: (row) =>
              [row.kunde_name1, row.kunde_name2, row.kunde_name3, row.kunde_name4]
                .filter(Boolean)
                .join(" "),
            accessorKey: ["kunde_name1"],
            accessorKeys: ["kunde_name1", "kunde_name2"],
            header: "Kunde",
          },
        ]),
    // {
    //   id: "actions",
    //   header: "Aktionen",
    //   size: 120,
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   Cell: ({ row }) => (
    //     <>
    //       {can_manage_users && (
    //         <IconButton
    //           size="small"
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             onRowEdit(row.original);
    //           }}
    //           title="Benutzer bearbeiten"
    //         >
    //           <EditIcon />
    //         </IconButton>
    //       )}
    //     </>
    //   ),
    // },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      enableTopToolbar={true}
      toolbarActions={toolbarActions}
      onRowClick={(row) => {
        if (can_manage_users && row?.id) {
          navigate(`/admin/users/${row.id}`);
        }
      }}
    />
  );
}

UsersTable.propTypes = {
  onRowEdit: PropTypes.func,
  onClickNewCommunication: PropTypes.func,
  onClickImport: PropTypes.func,
};
